// src/services/eventService.js
// Couche service — opérations Firebase pour les événements

import { db, storage } from '../firebase';
import {
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  collection,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  increment,
  onSnapshot,
  runTransaction,
  Timestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const eventService = {
  // ── Création ───────────────────────────────────────────
  async createEvent(data) {
    try {
      let imageUrl = data.imageUrl || '';
      if (data.imageFile) {
        imageUrl = await this.uploadEventImage(data.organizerId, data.imageFile);
      }

      const eventRef = doc(collection(db, 'events'));
      const event = {
        title: data.title,
        description: data.description,
        category: data.category,
        organizerId: data.organizerId,
        organizerName: data.organizerName || '',
        organizerPhoto: data.organizerPhoto || '',
        date: data.date,
        endDate: data.endDate || null,
        location: data.location || {
          name: '',
          address: '',
          city: '',
          postalCode: '',
          lat: null,
          lng: null,
        },
        online: data.online || false,
        imageUrl,
        maxAttendees: data.maxAttendees || null,
        price: data.price ?? 0,
        attendees: [],
        attendeeCount: 0,
        status: 'active',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await setDoc(eventRef, event);
      return { success: true, event: { id: eventRef.id, ...event } };
    } catch (err) {
      console.error('eventService.createEvent error:', err);
      return { success: false, error: "Erreur lors de la création de l'événement" };
    }
  },

  // ── Mise à jour ────────────────────────────────────────
  async updateEvent(eventId, data, userId) {
    try {
      const snap = await getDoc(doc(db, 'events', eventId));
      if (!snap.exists()) return { success: false, error: 'Événement introuvable' };
      if (snap.data().organizerId !== userId) {
        return { success: false, error: "Seul l'organisateur peut modifier cet événement" };
      }

      let imageUrl = data.imageUrl;
      if (data.imageFile) {
        imageUrl = await this.uploadEventImage(userId, data.imageFile);
      }

      const updateData = {
        ...(data.title !== undefined && { title: data.title }),
        ...(data.description !== undefined && { description: data.description }),
        ...(data.category !== undefined && { category: data.category }),
        ...(data.date !== undefined && { date: data.date }),
        ...(data.endDate !== undefined && { endDate: data.endDate }),
        ...(data.location !== undefined && { location: data.location }),
        ...(data.online !== undefined && { online: data.online }),
        ...(imageUrl !== undefined && { imageUrl }),
        ...(data.maxAttendees !== undefined && { maxAttendees: data.maxAttendees }),
        ...(data.price !== undefined && { price: data.price }),
        updatedAt: serverTimestamp(),
      };

      await updateDoc(doc(db, 'events', eventId), updateData);
      const updated = await getDoc(doc(db, 'events', eventId));
      return { success: true, event: { id: eventId, ...updated.data() } };
    } catch (err) {
      console.error('eventService.updateEvent error:', err);
      return { success: false, error: "Erreur lors de la mise à jour de l'événement" };
    }
  },

  // ── Annulation ─────────────────────────────────────────
  async cancelEvent(eventId, userId) {
    try {
      const snap = await getDoc(doc(db, 'events', eventId));
      if (!snap.exists()) return { success: false, error: 'Événement introuvable' };
      if (snap.data().organizerId !== userId) {
        return { success: false, error: "Seul l'organisateur peut annuler cet événement" };
      }

      await updateDoc(doc(db, 'events', eventId), {
        status: 'cancelled',
        updatedAt: serverTimestamp(),
      });
      return { success: true };
    } catch (err) {
      console.error('eventService.cancelEvent error:', err);
      return { success: false, error: "Erreur lors de l'annulation de l'événement" };
    }
  },

  // ── Lecture ─────────────────────────────────────────────
  async getEvent(eventId) {
    try {
      const snap = await getDoc(doc(db, 'events', eventId));
      if (!snap.exists()) return null;
      return { id: eventId, ...snap.data() };
    } catch (err) {
      console.error('eventService.getEvent error:', err);
      return null;
    }
  },

  async getEvents(filters = {}) {
    try {
      const constraints = [orderBy('date', 'asc')];

      if (filters.status) {
        constraints.unshift(where('status', '==', filters.status));
      }

      const snap = await getDocs(query(collection(db, 'events'), ...constraints));
      let list = [];
      snap.forEach((d) => {
        list.push({ id: d.id, ...d.data() });
      });

      if (filters.category) {
        list = list.filter((e) => e.category === filters.category);
      }

      return list;
    } catch (err) {
      console.error('eventService.getEvents error:', err);
      return [];
    }
  },

  async getUpcomingEvents(maxResults = 20) {
    try {
      const now = Timestamp.now();
      const snap = await getDocs(
        query(
          collection(db, 'events'),
          where('status', '==', 'active'),
          where('date', '>=', now),
          orderBy('date', 'asc'),
          firestoreLimit(maxResults)
        )
      );
      const list = [];
      snap.forEach((d) => {
        list.push({ id: d.id, ...d.data() });
      });
      return list;
    } catch (error) {
      console.error('getUpcomingEvents error:', error);
      return [];
    }
  },

  async getUserEvents(uid) {
    try {
      const snap = await getDocs(
        query(
          collection(db, 'events'),
          where('attendees', 'array-contains', uid),
          orderBy('date', 'asc')
        )
      );
      const list = [];
      snap.forEach((d) => {
        list.push({ id: d.id, ...d.data() });
      });
      return list;
    } catch (error) {
      console.error('getUserEvents error:', error);
      return [];
    }
  },

  async getPastEvents() {
    try {
      const now = Timestamp.now();
      const snap = await getDocs(
        query(
          collection(db, 'events'),
          where('status', '==', 'active'),
          where('date', '<', now),
          orderBy('date', 'desc')
        )
      );
      const list = [];
      snap.forEach((d) => {
        list.push({ id: d.id, ...d.data() });
      });
      return list;
    } catch (error) {
      console.error('getPastEvents error:', error);
      return [];
    }
  },

  // ── Inscription / Désinscription ───────────────────────
  async joinEvent(eventId, uid) {
    try {
      const eventRef = doc(db, 'events', eventId);
      await runTransaction(db, async (transaction) => {
        const snap = await transaction.get(eventRef);
        if (!snap.exists()) throw new Error('Événement introuvable');

        const data = snap.data();
        if (data.status === 'cancelled') throw new Error('Cet événement est annulé');
        if ((data.attendees || []).includes(uid)) throw new Error('Vous êtes déjà inscrit');
        if (data.maxAttendees && data.attendeeCount >= data.maxAttendees) {
          throw new Error('Événement complet');
        }

        transaction.update(eventRef, {
          attendees: arrayUnion(uid),
          attendeeCount: increment(1),
          updatedAt: serverTimestamp(),
        });
      });
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message || "Erreur lors de l'inscription" };
    }
  },

  async leaveEvent(eventId, uid) {
    try {
      const eventRef = doc(db, 'events', eventId);
      await runTransaction(db, async (transaction) => {
        const snap = await transaction.get(eventRef);
        if (!snap.exists()) throw new Error('Événement introuvable');

        if (!(snap.data().attendees || []).includes(uid)) {
          throw new Error("Vous n'êtes pas inscrit");
        }

        transaction.update(eventRef, {
          attendees: arrayRemove(uid),
          attendeeCount: increment(-1),
          updatedAt: serverTimestamp(),
        });
      });
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message || 'Erreur lors de la désinscription' };
    }
  },

  // ── Commentaires ───────────────────────────────────────
  async addComment(eventId, data) {
    try {
      const commentRef = doc(collection(db, 'events', eventId, 'comments'));
      const comment = {
        userId: data.userId,
        userName: data.userName || '',
        userPhoto: data.userPhoto || '',
        text: data.text,
        createdAt: serverTimestamp(),
      };
      await setDoc(commentRef, comment);
      return { success: true, comment: { id: commentRef.id, ...comment } };
    } catch (err) {
      console.error('eventService.addComment error:', err);
      return { success: false, error: "Erreur lors de l'ajout du commentaire" };
    }
  },

  subscribeToComments(eventId, callback) {
    const q = query(collection(db, 'events', eventId, 'comments'), orderBy('createdAt', 'asc'));
    return onSnapshot(
      q,
      (snap) => {
        const comments = [];
        snap.forEach((d) => {
          comments.push({ id: d.id, ...d.data() });
        });
        callback(comments);
      },
      (err) => console.error('subscribeToComments onSnapshot error:', err)
    );
  },

  // ── Upload image ───────────────────────────────────────
  async uploadEventImage(userId, file) {
    const blob = await new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(img.src);
        let w = img.width,
          h = img.height;
        const max = 800;
        if (w > h) {
          if (w > max) {
            h = (h * max) / w;
            w = max;
          }
        } else {
          if (h > max) {
            w = (w * max) / h;
            h = max;
          }
        }
        canvas.width = w;
        canvas.height = h;
        ctx.drawImage(img, 0, 0, w, h);
        canvas.toBlob(resolve, 'image/jpeg', 0.7);
      };
      img.onerror = () => {
        URL.revokeObjectURL(img.src);
        reject(new Error('Format image invalide'));
      };
      img.src = URL.createObjectURL(file);
    });
    const sRef = ref(storage, `event_pictures/${userId}/event_${Date.now()}.jpg`);
    await uploadBytes(sRef, blob);
    return getDownloadURL(sRef);
  },
};

export default eventService;
